import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
	Box,
	Button,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	TextField,
	Theme,
	Typography,
	Tooltip,
	useMediaQuery,
	Paper,
} from '@mui/material';
import { EntityType, Entry, PressedKey, PropertyDefinition } from '../types';
import {PROPERTY_DEFINITION_TYPES, REPOSITORIES_BY_ENTITY_TYPE} from '../constants';
import {EditFormDialog, useEditForm} from '../components/EditEntryForm';
import ConfirmDeleteEntryDialog, {useConfirmDeleteEntryDialog} from '../components/ConfirmDeleteEntryDialog';
import {assignRandomId, getRepositorySingleUnitLabel, sortEntryByRank} from '../common/util';
import {useGenericListItemMenu} from '../components/GenericListItemMenu';
import {useAppSelector} from '../hooks';
import {isAddRepositoryPropertyDialogOpenSetState, isEditRepositoryDialogOpenSetState, selectIsAddRepositoryPropertyDialogOpen, selectIsAnyDialogOpen, selectIsEditRepositoryDialogOpen} from './formDataSlice';
import {useContentNavigationEffects} from '../hooks/useContentNavigationEffects';
import {useKeyboardEffects} from '../components/KeyboardEffects';
import { RowMenuOption, RowMenuItem, RowMenuOptionEdit, RowMenuOptionDelete, RowMenuOptionReorder, RowMenuOptionEffect } from '../components/EntryMenu';
import {useDispatch} from 'react-redux';
import { navigateRollOutward } from './navigationSlice';
import AddRepositoryPropertyDialog from '../components/AddRepositoryPropertyDialog';
import EntryMenu from '../components/EntryMenu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {entriesUpdateMany, propertyDefinitionsAddMany, recordDeleteLocalAction, selectLocationId, selectAllRepositories, selectAllPropertyDefinitions, selectIsEntriesHydrated, setActiveRepositoryId} from './contentSlice';
import {selectIsAuthenticated} from './authSlice';
import { useDeleteRepositoryMutation, useGetRepositoriesAiDiscoveryQuery, useGetRepositoryByIdQuery, useUpdateRepositoryMutation } from '../services/repositoryEntries';
import useAuth from '../components/Auth';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { selectCurrentLocationEntriesMaxRank } from './repositoryEntryViewSlice';
import { Helmet } from 'react-helmet';
import config from '../common/config';
import WarningIcon from '@mui/icons-material/Warning';
import { DataTypeAvatar } from '../components/DataTypeAvatar';
import { usePropertyDefinitionCreateMutation, usePropertyDefinitionDeleteMutation, usePropertyDefinitionsUpdateManyMutation } from '../services/propertyDefinitions';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { BookmarkColorPopover } from '../components/BookmarkColorPopover';
import ShareRepositoryDialog from '../components/ShareRepositoryDialog';
import { isNotFoundQueryError } from '../common/queryErrors';
import NotFoundView from './NotFoundView';
import { RepositoryAccessKeyType } from '../services/repositoryAccessKeys';

const RepositorySettingsView = () => {
	useContentNavigationEffects();
	const dispatch = useDispatch();
	const setKeyboardMappings = useKeyboardEffects();

	const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen);
	const allRepositories = useAppSelector(selectAllRepositories);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const {isEditDialogOpen, editFormEntry, closeEditDialog, onEditFormSubmit} = useEditForm();
	const {isConfirmDeleteDialogOpen, confirmDeleteDialogEntry, closeConfirmDeleteDialog, openConfirmDeleteDialog} = useConfirmDeleteEntryDialog();
	
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const [propertyDefinitionsUpdateManyMutation] = usePropertyDefinitionsUpdateManyMutation();
	const [propertyDefinitionDeleteMutation] = usePropertyDefinitionDeleteMutation();
	const [updateRepositoryMutation] = useUpdateRepositoryMutation();
	const [deleteRepositoryMutation] = useDeleteRepositoryMutation();
	const locationId = useAppSelector(selectLocationId);
	const {isAuthenticating} = useAuth();
	const navigate = useNavigate();
	const currentMaxRank = useAppSelector(selectCurrentLocationEntriesMaxRank);
	const parentId = useAppSelector(selectLocationId);
	const [propertyDefinitionCreateMutation] = usePropertyDefinitionCreateMutation();
	const [editRepositoryTitle, setEditRepositoryTitle] = useState('');
	const [editRepositorySingular, setEditRepositorySingular] = useState('');
	const isAddRepositoryPropertyDialogOpen = useAppSelector(selectIsAddRepositoryPropertyDialogOpen);
	const isEditRepositoryDialogOpen = useAppSelector(selectIsEditRepositoryDialogOpen);
	const [bookmarkColorAnchor, setBookmarkColorAnchor] = useState<HTMLElement | null>(null);
	const [shareDialogOpen, setShareDialogOpen] = useState(false);

	const { data: repositoryByIdResponse, error: repositoryByIdError } = useGetRepositoryByIdQuery(locationId as string, {
		skip: !isAuthenticated || isAuthenticating || !locationId,
	});
	const isDebugMcpMode = process.env.REACT_APP_DEBUG_MCP_MODE === 'true';
	if (isDebugMcpMode) {
		console.warn('DEBUG MCP MODE is enabled. AI tool data will be fetched.');
	}
	useGetRepositoriesAiDiscoveryQuery(undefined, {
		skip: !isAuthenticated || isAuthenticating || !locationId || !isDebugMcpMode,
	});
	const repository = useMemo(() => {
		if (isAuthenticated) {
			return repositoryByIdResponse?.repository;
		}
		return allRepositories.find((entry) => entry.id === locationId) ?? null;
	}, [repositoryByIdResponse?.repository, isAuthenticated, allRepositories, locationId]);
	const repositoryPropertyDefinitions = useMemo(() => {
		const raw = isAuthenticated
			? repositoryByIdResponse?.propertyDefinitions ?? []
			: allPropertyDefinitions.filter((entry) => entry.parentId === locationId);
		return [...raw].sort(sortEntryByRank);
	}, [repositoryByIdResponse?.propertyDefinitions, isAuthenticated, allPropertyDefinitions, locationId]);
	const menuAnchorEl = useRef<Element | null>(null);

	const {menuEntry, setMenuEntry, closeListItemMenu, handleMenuEditBtnClick, handleMenuDeleteBtnClick} = useGenericListItemMenu({ entries: repositoryPropertyDefinitions as Entry[] });

	const openListItemMenu = (item: RowMenuItem, el?: Element) => {
		setMenuEntry(item as Entry);
		if (el) {
			menuAnchorEl.current = el;
		}
	};

	const editDialogTitle = useMemo(() => editFormEntry ? `Edit ${getRepositorySingleUnitLabel(REPOSITORIES_BY_ENTITY_TYPE[editFormEntry.type]).toLowerCase()}` : '', [editFormEntry]);
	const confirmDeleteDialogTitle = useMemo(() => confirmDeleteDialogEntry ? `Delete ${getRepositorySingleUnitLabel(REPOSITORIES_BY_ENTITY_TYPE[confirmDeleteDialogEntry.type]).toLowerCase()}?` : '', [confirmDeleteDialogEntry]);

	const handleKeyPressEscape = useCallback(() => {
		if (isAnyDialogOpen) {
			return;
		}
		dispatch(navigateRollOutward());
	}, [dispatch, isAnyDialogOpen]);

	const handleKeyPressSpace = useCallback(() => {
		if (isAnyDialogOpen) {
			return;
		}
		dispatch(isAddRepositoryPropertyDialogOpenSetState(true));
	}, [dispatch, isAnyDialogOpen]);

	useEffect(() => {
		setKeyboardMappings({
			[PressedKey.Escape]: handleKeyPressEscape,
			[PressedKey.Space]: handleKeyPressSpace,
		});
		return () => {
			setKeyboardMappings(null);
		};
	}, [handleKeyPressEscape, handleKeyPressSpace, setKeyboardMappings]);

	// Reordering state: drag (mouse) and reorder-by-click (menu → tap target)
	const [dragItemId, setDragItemId] = useState<string | null>(null);
	const [reorderItemId, setReorderItemId] = useState<string | null>(null);
	const [localOrder, setLocalOrder] = useState<string[] | null>(null);
	const isTouchReorder = Boolean(reorderItemId);
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const rowMenuOptions: RowMenuOption[] = [
		{
			content: RowMenuOptionEdit,
			onClick: (item: RowMenuItem) => handleMenuEditBtnClick(item.id),
			key: 'editOption',
		},
		...(isMobile ? [{
			content: RowMenuOptionReorder,
			onClick: RowMenuOptionEffect.Reorder,
			key: 'reorderOption',
		}]: []),
		{
			content: RowMenuOptionDelete,
			onClick: (item: RowMenuItem) => handleMenuDeleteBtnClick(item.id),
			key: 'deleteOption',
		}
	];

	const entryIds = useMemo(
		() => repositoryPropertyDefinitions.map((def) => def.id),
		[repositoryPropertyDefinitions]
	);

	const defById = useMemo(() => {
		const map = new Map<string, (typeof repositoryPropertyDefinitions)[number]>();
		repositoryPropertyDefinitions.forEach((def) => map.set(def.id, def));
		return map;
	}, [repositoryPropertyDefinitions]);

	const currentOrder = localOrder ?? entryIds;

	const commitReorder = useCallback(() => {
		if (!localOrder) return;
		const updates = localOrder.map((id, rank) => ({ id, rank }));
		if (isAuthenticated && locationId) {
			propertyDefinitionsUpdateManyMutation({repositoryId: locationId, records: updates});
		} else {
			dispatch(entriesUpdateMany(updates));
		}
		setReorderItemId(null);
	}, [localOrder, isAuthenticated, locationId, propertyDefinitionsUpdateManyMutation, dispatch]);

	const commitOrder = useCallback((order: string[]) => {
		const updates = order.map((id, rank) => ({ id, rank }));
		if (isAuthenticated && locationId) {
			propertyDefinitionsUpdateManyMutation({repositoryId: locationId, records: updates});
		} else {
			dispatch(entriesUpdateMany(updates));
		}
		setReorderItemId(null);
	}, [isAuthenticated, locationId, propertyDefinitionsUpdateManyMutation, dispatch]);

	/** Clear drag overlay once canonical order matches (same idea as EntriesTable / EntriesList). */
	useEffect(() => {
		if (!localOrder?.length) {
			return;
		}
		if (entryIds.length !== localOrder.length) {
			return;
		}
		if (entryIds.every((id, i) => id === localOrder[i])) {
			setLocalOrder(null);
		}
	}, [entryIds, localOrder]);

	const handleRowMenuReorderClick = useCallback((item: RowMenuItem) => {
		closeListItemMenu();
		setReorderItemId(item.id);
		setLocalOrder(entryIds);
	}, [closeListItemMenu, entryIds]);

	const handleReorderTargetClick = useCallback((e: React.MouseEvent, targetId: string) => {
		if (!reorderItemId) return;
		if ((e.target as HTMLElement).closest('button')) return;
		if (reorderItemId === targetId) {
			setReorderItemId(null);
			setLocalOrder(null);
			return;
		}
		const order = localOrder ?? entryIds;
		const fromIndex = order.indexOf(reorderItemId);
		const toIndex = order.indexOf(targetId);
		if (fromIndex === -1 || toIndex === -1) return;
		const next = [...order];
		next.splice(fromIndex, 1);
		next.splice(toIndex, 0, reorderItemId);
		commitOrder(next);
	}, [reorderItemId, localOrder, entryIds, commitOrder]);

	const handleAddDialogSubmit = useCallback((formData: any) => {
		const newEntryPayload = assignRandomId({
			...formData,
			parentId: parentId ?? null,
			rank: currentMaxRank + 1,
		}) as PropertyDefinition;

		if (isAuthenticated) {
			propertyDefinitionCreateMutation(newEntryPayload);
		} else {
			dispatch(propertyDefinitionsAddMany([newEntryPayload]));
		}
		dispatch(isAddRepositoryPropertyDialogOpenSetState(false));
	}, [dispatch, isAuthenticated, propertyDefinitionCreateMutation, currentMaxRank, parentId]);

	const handleMouseUp = useCallback(() => {
		if (!dragItemId) return;
		setDragItemId(null);
		commitReorder();
	}, [dragItemId, commitReorder]);

	useEffect(() => {
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseUp]);

	const handleRowMouseDown = useCallback(
		(id: string) => {
			setDragItemId(id);
			setLocalOrder(entryIds);
		},
		[entryIds]
	);

	const handleRowMouseEnter = useCallback(
		(id: string) => {
			if (!dragItemId) return;
			if (dragItemId === id) return;
			const order = localOrder ?? entryIds;
			const fromIndex = order.indexOf(dragItemId);
			const toIndex = order.indexOf(id);
			if (fromIndex === -1 || toIndex === -1) return;
			const next = [...order];
			next.splice(fromIndex, 1);
			next.splice(toIndex, 0, dragItemId);
			setLocalOrder(next);
		},
		[dragItemId, localOrder, entryIds]
	);

	const handleDeleteBtnClick = useCallback(() => {
		if (repository) {
			openConfirmDeleteDialog(repository as unknown as Entry);
		}
	}, [repository, openConfirmDeleteDialog]);

	const handleConfirmDeleteSubmit = useCallback(() => {
		const entry = confirmDeleteDialogEntry as Entry | null;
		if (!entry) {
			return;
		}
		const {id} = entry;
		const isPropertyDefinition = PROPERTY_DEFINITION_TYPES.includes(entry.type as any);

		if (isPropertyDefinition) {
			if (isAuthenticated) {
				propertyDefinitionDeleteMutation({
					id,
					repositoryId: locationId ?? undefined,
				});
			} else {
				dispatch(recordDeleteLocalAction(id));
			}
		} else {
			if (isAuthenticated) {
				deleteRepositoryMutation(id);
			} else {
				dispatch(recordDeleteLocalAction(id));
			}
		}

		if (entry.type === EntityType.Repository) {
			navigate('/');
		}
		closeConfirmDeleteDialog();
	}, [
		confirmDeleteDialogEntry,
		isAuthenticated,
		propertyDefinitionDeleteMutation,
		dispatch,
		navigate,
		closeConfirmDeleteDialog,
		locationId,
	]);
	
	const handleOpenEditRepositoryDialog = () => {
		if (!repository) return;
		setEditRepositoryTitle(repository.title);
		setEditRepositorySingular(repository.singular ?? '');
		dispatch(isEditRepositoryDialogOpenSetState(true));
	};

	const handleCloseEditRepositoryDialog = () => {
		dispatch(isEditRepositoryDialogOpenSetState(false));
	};

	const handleEditRepositorySubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!repository) return;

		const title = editRepositoryTitle.trim();
		const singular = editRepositorySingular.trim();
		if (!title) {
			return;
		}

		const payload = {id: repository.id, title, singular};

		if (isAuthenticated) {
			updateRepositoryMutation(payload);
		} else {
			dispatch(entriesUpdateMany([payload as any]));
		}

		dispatch(isEditRepositoryDialogOpenSetState(false));
	};

	const closeBookmarkColorPopover = useCallback(() => {
		setBookmarkColorAnchor(null);
	}, []);

	const handlePickBookmarkColor = useCallback(
		(hex: string) => {
			if (!repository) return;
			const payload = {
				id: repository.id,
				title: repository.title,
				singular: repository.singular ?? '',
				color: hex,
				isBookmarked: true,
			};
			if (isAuthenticated) {
				updateRepositoryMutation(payload);
			} else {
				dispatch(entriesUpdateMany([{ ...payload, type: EntityType.Repository }]));
			}
			closeBookmarkColorPopover();
		},
		[repository, isAuthenticated, updateRepositoryMutation, dispatch, closeBookmarkColorPopover],
	);

	const handleClearBookmark = useCallback(() => {
		if (!repository) return;
		const payload = {
			id: repository.id,
			title: repository.title,
			singular: repository.singular ?? '',
			color: null as string | null,
			isBookmarked: false,
		};
		if (isAuthenticated) {
			updateRepositoryMutation(payload);
		} else {
			dispatch(entriesUpdateMany([{ ...payload, type: EntityType.Repository }]));
		}
		closeBookmarkColorPopover();
	}, [repository, isAuthenticated, updateRepositoryMutation, dispatch, closeBookmarkColorPopover]);


	useEffect(() => {
		dispatch(setActiveRepositoryId(repository?.id ?? null));
		return () => {
			dispatch(setActiveRepositoryId(null));
		};
	}, [dispatch, repository?.id]);

	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const isNotFound = isAuthenticated ? isNotFoundQueryError(repositoryByIdError) : isEntriesHydrated && !repository;
	if (isNotFound) {
		return <NotFoundView />;
	}

	return <>
		{repository && <Helmet>
			<title>{repository.title} (Settings) - {config.appName}</title>
		</Helmet>}
		<Box minHeight='calc(100dvh - 72px)' display='flex' flexDirection='column'>
			<Box flex={1}>
				<Container maxWidth='sm' sx={{mb: 5}}>
					<Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
						<Box display='flex' flexDirection='column' onClick={handleOpenEditRepositoryDialog} sx={{cursor: 'pointer', '&:hover': {backgroundColor: 'action.hover'}}}>
							<Typography variant='caption' color='text.secondary'>Collection</Typography>
							<Typography variant='h4'>{repository?.title}</Typography>
						</Box>
						{repository && (
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Tooltip title='Bookmark this collection' disableInteractive>
									<IconButton
										aria-label='Choose bookmark color'
										onClick={(e) => setBookmarkColorAnchor(e.currentTarget)}
									>
										{repository.color ? (
											<BookmarkIcon sx={{ fontSize: '2rem', color: repository.color }} />
										) : (
											<BookmarkBorderIcon sx={{ fontSize: '2rem' }} />
										)}
									</IconButton>
								</Tooltip>
								<BookmarkColorPopover
									open={Boolean(bookmarkColorAnchor)}
									anchorEl={bookmarkColorAnchor}
									onClose={closeBookmarkColorPopover}
									onPickColor={handlePickBookmarkColor}
									onClearBookmark={handleClearBookmark}
								/>
							</Box>
						)}
					</Box>
					{repository && (
						<Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, my: 2}}>
							<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
								<Box onClick={handleOpenEditRepositoryDialog} sx={{cursor: 'pointer', '&:hover': {backgroundColor: 'action.hover'}}}>
									<Typography variant='subtitle2' color='text.secondary'>
								Singular unit label
									</Typography>
									<Typography variant='body1'>
										{repository.singular}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
					<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
						<Typography variant='subtitle2' color='text.secondary'>
						Data fields
						</Typography>
						{!repositoryPropertyDefinitions.length && <Tooltip title='Please define some data fields to begin using this collection.' placement='right' disableInteractive>
							<WarningIcon color='warning' fontSize='small' />
						</Tooltip>}
					</Box>
					<List disablePadding sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
						{currentOrder.map((id) => {
							const def = defById.get(id);
							if (!def) return null;
							const isReorderHighlight = dragItemId === def.id || reorderItemId === def.id;
							return (
								<ListItem
									component={Paper}
									sx={{
										backgroundColor: isReorderHighlight ? 'action.selected' : 'transparent',
										transition: 'background-color 0.2s ease-in-out',
										cursor: isTouchReorder ? 'pointer' : undefined,
										boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
									}}
									key={def.id}
									onMouseEnter={() => handleRowMouseEnter(def.id)}
									onClick={isTouchReorder ? (e) => handleReorderTargetClick(e, def.id) : undefined}
									secondaryAction={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											{!isMobile && (
												<Tooltip title='Drag to reorder' disableInteractive>
													<IconButton
														onMouseDown={(e) => {
															e.stopPropagation();
															handleRowMouseDown(def.id);
														}}
														aria-label="Reorder data field"
													>
														<DragIndicatorIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
											<IconButton
												edge="end"
												onClick={(e) => {
													e.stopPropagation();
													openListItemMenu(def as unknown as RowMenuItem, e.currentTarget);
												}}
												aria-label="Data field menu"
											>
												<MoreHorizIcon fontSize="small" />
											</IconButton>
										</Box>
									}
								>
									<ListItemAvatar>
										<DataTypeAvatar type={def.type} />
									</ListItemAvatar>
									<ListItemText primary={def.label} secondary={<Box display='flex' alignItems='center' gap={1}>
										<span>{def.slug}</span>
									</Box>} />
									{def.required && def.type !== EntityType.PropertyDefinitionBoolean && <ListItemText sx={{ textAlign: 'right', mr: 6 }} secondary='Required' />}
								</ListItem>
							);
						})}
					</List>
					<Button sx={{mt: 1}} onClick={() => dispatch(isAddRepositoryPropertyDialogOpenSetState(true))} startIcon={<AddIcon />}>
						New data field
					</Button>
				</Container>
			</Box>
			{repository && (
				<Container maxWidth={isSmScreen ? 'sm' : false}>
					<Box display='flex' flexDirection='column' alignItems='flex-end' gap={2} mb={5}>
						<Button onClick={() => navigate(`/collection/${repository?.id}`)} aria-label='Close' endIcon={<StorageIcon />} color='inherit'>
							To the list
						</Button>
						{isAuthenticated && (
							<Button onClick={() => setShareDialogOpen(true)} aria-label='Share options' endIcon={<ShareIcon />} color='inherit'>
								Share schema
							</Button>
						)}
						<Button onClick={handleDeleteBtnClick} aria-label='Delete collection' color='error' endIcon={<DeleteIcon />}>
							Delete collection
						</Button>
					</Box>
				</Container>
			)}
		</Box>
		<Dialog
			open={isEditRepositoryDialogOpen}
			onClose={handleCloseEditRepositoryDialog}
			maxWidth='xs'
			fullWidth
		>
			<DialogTitle>Edit collection</DialogTitle>
			<form onSubmit={handleEditRepositorySubmit} autoComplete='off'>
				<DialogContent>
					<Box display='flex' flexDirection='column' gap={2} mt={1}>
						<TextField
							label='Title'
							value={editRepositoryTitle}
							onChange={(event) => setEditRepositoryTitle(event.target.value)}
							variant='standard'
							fullWidth
							required
							autoFocus
						/>
						<TextField
							label='Singular'
							value={editRepositorySingular}
							onChange={(event) => setEditRepositorySingular(event.target.value)}
							variant='standard'
							fullWidth
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseEditRepositoryDialog}>
						Cancel
					</Button>
					<Button type='submit' variant='contained'>
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
		<AddRepositoryPropertyDialog
			open={isAddRepositoryPropertyDialogOpen}
			onClose={() => dispatch(isAddRepositoryPropertyDialogOpenSetState(false))}
			onSubmit={handleAddDialogSubmit}
		/>
		<EntryMenu
			options={rowMenuOptions}
			anchorEl={menuAnchorEl.current}
			onClose={closeListItemMenu}
			item={menuEntry}
			onRowReorderClick={handleRowMenuReorderClick}
		/>
		<EditFormDialog
			open={isEditDialogOpen}
			handleClose={closeEditDialog}
			handleSubmit={onEditFormSubmit}
			title={editDialogTitle}
		/>
		<ConfirmDeleteEntryDialog
			open={isConfirmDeleteDialogOpen}
			handleClose={closeConfirmDeleteDialog}
			handleSubmit={handleConfirmDeleteSubmit}
			entry={confirmDeleteDialogEntry}
			title={confirmDeleteDialogTitle}
		/>
		{repository && (
			<ShareRepositoryDialog
				open={shareDialogOpen}
				onClose={() => setShareDialogOpen(false)}
				repositoryId={repository.id}
				repositoryTitle={repository.title}
				type={RepositoryAccessKeyType.Schema}
			/>
		)}
	</>;
};

export default RepositorySettingsView;